import Vue from 'vue'

import { Colors, Services, generateUniqueKey } from '@sigma-frontend-libs/commons'

import { buttonModes, components, labelModes, permissionColors, permissionPresets, sizes } from '@/utils'

import button from '@/components/button'
import arrow from '@/components/misc/arrow'
import preloader from '@/components/preloader'
import tag from '@/components/tag'

function renderItemTitle(h, header) {
  return h(
    tag,
    {
      props: {
        label: this.getTranslate(`${Services.permissions}.services.${header}`),
        tooltip: `${this.currentTab}.${header}`,
        as: 'select'
      }
    }
  )
}
function renderItemHeaderAction(h, child) {
  const value = this.items?.[child.key]
  if (value !== undefined) {
    return h(
      tag,
      {
        props: {
          label: this.getTranslate(`${Services.permissions}.labels.${child.action}`),
          size: sizes.tiny,
          color: permissionColors[value],
          tooltip: value.toString()
        }
      }
    )
  }
}
function renderItemHeaderActionsForDefault(h, children) {
  return children.map(child => {
    return renderItemHeaderAction.call(this, h, child)
  })
}
function renderItemHeaderActionsByTab(h, children) {
  return renderItemHeaderActionsForDefault.call(this, h, children)
}
function renderItemHeaderActions(h, children) {
  if (Array.isArray(children) && children.length) {
    return h(
      'div',
      {
        class: `${Services.permissions}-item__header-actions`
      },
      [ renderItemHeaderActionsByTab.call(this, h, children) ]
    )
  }
}
function renderItemHeaderContent(h, header, children) {
  return h(
    'div',
    {
      class: `${Services.permissions}-item__header-content`
    },
    [
      renderItemTitle.call(this, h, header),
      renderItemHeaderActions.call(this, h, children)
    ]
  )
}
function renderItemHeader(h, header, children, expanded) {
  return h(
    'div',
    {
      class: `${Services.permissions}-item__header`
    },
    [
      renderItemHeaderContent.call(this, h, header, children),
      h(arrow, { props: { expanded } })
    ]
  )
}
function renderPermissionName(h, child) {
  return h(
    tag,
    {
      props: {
        label: this.getTranslate(`${Services.permissions}.labels.${child.action}`),
        tooltip: child.key,
        as: 'select'
      }
    }
  )
}
function renderPermissionAccept(h, child, accept) {
  const option = child.options.find(option => {
    return option === accept
  })
  const value = this.items?.[child.key]
  const isActive = value === option
  const disabled = accept !== option

  let opacity = 1
  if (disabled) {
    opacity = 0.5
  }

  let mode = buttonModes.flat
  let color = Colors.grey
  if (isActive) {
    mode = buttonModes.filled
    color = permissionColors[accept]
  }

  return h(
    button,
    {
      style: { opacity },
      props: {
        label: accept,
        size: sizes.tiny,
        mode,
        color,
        tooltip: this.getTranslate(`${Services.permissions}.accepts.${accept}`),
        loading: this.loading.update[`${child.key}:${accept}`],
        disabled
      },
      on: {
        click: () => {
          if (value !== accept) {
            Vue.set(this.loading.update, `${child.key}:${accept}`, true)
          }
          this.updatePermission(child.key, accept)
        }
      }
    }
  )
}
function renderPermissionAccepts(h, child) {
  return h(
    'div',
    {
      class: `${Services.permissions}-item__permission-accepts`
    },
    [
      Object.values(this.permissionAccepts).map(accept => {
        return renderPermissionAccept.call(this, h, child, accept)
      })
    ]
  )
}
function renderPermission(h, child) {
  return h(
    'div',
    {
      class: `${Services.permissions}-item__permission`
    },
    [
      renderPermissionName.call(this, h, child),
      renderPermissionAccepts.call(this, h, child)
    ]
  )
}
function renderItemBody(h, children) {
  return h(
    'div',
    {
      class: `${Services.permissions}-item__body`
    },
    [
      children.map(child => {
        return renderPermission.call(this, h, child)
      })
    ]
  )
}
function renderItem(h, header, children) {
  return h(
    components['expansion-panel'],
    {
      scopedSlots: {
        header: ({ expanded }) => {
          return renderItemHeader.call(this, h, header, children, expanded)
        },
        default: () => {
          return renderItemBody.call(this, h, children)
        }
      },
      key: generateUniqueKey()
    }
  )
}
function renderItems(h, headers, items) {
  if (headers.length) {
    return h(
      components['expansion-panels'],
      {
        props: {
          outline: true,
          rounded: true
        }
      },
      [
        headers.map(header => {
          return renderItem.call(this, h, header, items[header])
        })
      ]
    )
  }

  return h(components.empty)
}
function renderGranularItemHeader(h, header, expanded) {
  const [ origin, service ] = header.split('.')

  let originColor = Colors.primary
  if (origin === 'me') {
    originColor = Colors.success
  }

  return h(
    'div',
    {
      class: `${Services.permissions}-item-granular__header`
    },
    [
      h(
        'div',
        {
          class: `${Services.permissions}-item-granular__header-content`
        },
        [
          h(
            tag,
            {
              props: {
                label: this.getTranslate(`${Services.permissions}.services.${service}`),
                as: 'select'
              }
            }
          ),
          h(
            tag,
            {
              props: {
                label: this.getTranslate(`${Services.permissions}.origins.${origin}`),
                tooltip: this.getTranslate(`${Services.permissions}.tooltips.origins.${origin}`),
                size: sizes.tiny,
                color: originColor
              }
            }
          )
        ]
      ),
      h(arrow, { props: { expanded } })
    ]
  )
}
function renderGranularItems(h, items) {
  return h(
    'div',
    {
      class: `${Services.permissions}-item-granular__body`
    },
    [ renderItems.call(this, h, Object.keys(items), items) ]
  )
}
function renderGranular(h, headers, tab) {
  return h(
    components['expansion-panels'],
    {
      props: {
        outline: true,
        rounded: true
      }
    },
    [
      headers.map(header => {
        const items = this.getGranularItems(tab, header)

        return h(
          components['expansion-panel'],
          {
            scopedSlots: {
              header: ({ expanded }) => {
                return renderGranularItemHeader.call(this, h, header, expanded)
              },
              default: () => {
                return renderGranularItems.call(this, h, items)
              }
            }
          }
        )
      })
    ]
  )
}
function renderItemsByTab(h, tab) {
  const items = this.getItems(tab)
  const headers = Object.keys(items)
  switch (tab) {
    case 'granular': return renderGranular.call(this, h, headers, tab)
    default: return renderItems.call(this, h, headers, items)
  }
}

function renderPreloader(h) {
  return h(
    'div',
    {
      class: 'preloader__holder pa-5'
    },
    [ h(preloader) ]
  )
}

function renderTabsHeader(h) {
  return h(
    components['tabs-header'],
    {
      props: {
        value: this.currentTab,
        tabs: this.tabs.map(key => {
          return {
            key,
            title: this.getTranslate(`${Services.permissions}.tabs.${key}`)
          }
        })
      },
      on: {
        input: event => {
          this.currentTab = event
        }
      }
    }
  )
}
function renderSearch(h) {
  return h(
    components['text-field'],
    {
      props: {
        value: this.search,
        label: this.getTranslate('misc.search'),
        defaultValue: '',
        labelMode: labelModes.hidden,
        dense: true,
        clearable: true,
        details: false
      },
      on: {
        input: event => {
          this.search = event
        }
      }
    }
  )
}
function renderHeader(h) {
  if (this.items) {
    return h(
      'div',
      {
        class: `${Services.permissions}-header`
      },
      [
        renderTabsHeader.call(this, h),
        renderSearch.call(this, h)
      ]
    )
  }
}
function renderTab(h, tab) {
  return h(
    'div',
    {
      class: `${Services.permissions}-tab`,
      key: tab
    },
    [ renderItemsByTab.call(this, h, tab) ]
  )
}
function renderTabs(h) {
  return h(
    components.tabs,
    {
      props: { value: this.currentTab },
      scopedSlots: {
        body: () => {
          return this.tabs.map(tab => {
            return renderTab.call(this, h, tab)
          })
        }
      }
    }
  )
}
function renderBody(h) {
  return h(
    'div',
    {
      class: Services.permissions
    },
    [
      renderHeader.call(this, h),
      renderTabs.call(this, h)
    ]
  )
}

export default function(h) {
  if (this.checkPermissions(`advanced.${Services.users}.setPermissions`, permissionPresets.resellerUp)) {
    switch (this.loading.find) {
      case true: return renderPreloader.call(this, h)
      case false: {
        if (this.items) {
          return renderBody.call(this, h)
        } else {
          return h(components.empty)
        }
      }
    }
  }
}
